// frontend/app/(nurse)/jobs/page.tsx
"use client";

import { useState } from "react";
import Link from "next/link";
import {
  Search,
  MapPin,
  Clock,
  Calendar,
  DollarSign,
  AlertTriangle,
  Briefcase,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Bookmark,
  ArrowUpDown,
  ExternalLink,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useJobs, useApplyToJob, JobItem } from "@/lib/hooks/useJobs";
import { useAppliedJobIds } from "@/lib/hooks/useNurseApplications";
import { NotificationsBell } from "@/components/notifications";

// =============================================================================
// Types
// =============================================================================

type ShiftFilter = "ANY" | "DAYS" | "NIGHTS" | "ROTATING";
type SortOption = "relevance" | "pay_high" | "pay_low" | "date_new" | "date_old";

// =============================================================================
// Job Card Component
// =============================================================================

interface JobCardProps {
  job: JobItem;
  applied: boolean;
  onApply: (jobId: string) => void;
  isApplying: boolean;
}

function JobCard({ job, applied, onApply, isApplying }: JobCardProps) {
  const rate = job.nurse_base_rate_hour ?? null;
  const canApply = (job.status === undefined || job.status === "OPEN") && !applied && !isApplying;

  return (
    <article className="rounded-lg border border-slate-200 bg-white p-5 transition-shadow hover:shadow-md">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        {/* Job Info */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              href={`/jobs/${job.id}`}
              className="text-base font-semibold text-slate-900 hover:text-emerald-600 transition-colors"
            >
              {job.title}
            </Link>
            {job.is_urgent && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                <AlertTriangle className="h-3 w-3" />
                Urgent
              </span>
            )}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
            <span className="flex items-center gap-1">
              <Briefcase className="h-4 w-4 text-slate-400" />
              {job.facility_name}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4 text-slate-400" />
              {job.city}, {job.state}
            </span>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
            <span>{job.specialty}</span>
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {job.shift_type} ({job.shift_hours}h)
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {job.contract_weeks} weeks
            </span>
            {job.start_date && (
              <span>
                Starts: {new Date(job.start_date).toLocaleDateString()}
              </span>
            )}
          </div>

          {rate != null && (
            <div className="mt-3 flex items-center gap-1 text-sm font-semibold text-slate-800">
              <DollarSign className="h-4 w-4 text-emerald-600" />
              Est. ${rate.toFixed(0)}/hr
              {job.housing_stipend_week && (
                <span className="ml-2 text-xs font-normal text-slate-500">
                  + ${job.housing_stipend_week}/wk housing
                </span>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <Link
              href={`/jobs/${job.id}`}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              View Details
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
            <button
              type="button"
              disabled={!canApply}
              onClick={() => onApply(job.id)}
              className={cn(
                "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors",
                canApply
                  ? "bg-emerald-600 text-white hover:bg-emerald-500"
                  : "cursor-not-allowed bg-slate-100 text-slate-500"
              )}
            >
              {applied
                ? "Applied"
                : isApplying
                ? "Applying..."
                : "Quick Apply"}
            </button>
          </div>
          {!applied && (
            <p className="max-w-[240px] text-right text-xs text-slate-500">
              Your compliance is checked when reviewed
            </p>
          )}
        </div>
      </div>
    </article>
  );
}

// =============================================================================
// Advanced Filters Component
// =============================================================================

interface AdvancedFiltersProps {
  q: string;
  setQ: (v: string) => void;
  state: string;
  setState: (v: string) => void;
  specialty: string;
  setSpecialty: (v: string) => void;
  shiftType: ShiftFilter;
  setShiftType: (v: ShiftFilter) => void;
  minRate: number | undefined;
  setMinRate: (v: number | undefined) => void;
  isUrgent: boolean;
  setIsUrgent: (v: boolean) => void;
  onSearch: () => void;
  isLoading: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}

function AdvancedFilters({
  q,
  setQ,
  state,
  setState,
  specialty,
  setSpecialty,
  shiftType,
  setShiftType,
  minRate,
  setMinRate,
  isUrgent,
  setIsUrgent,
  onSearch,
  isLoading,
  isExpanded,
  onToggle,
}: AdvancedFiltersProps) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      {/* Quick Search Bar */}
      <div className="p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSearch();
          }}
          className="flex gap-3"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by keyword, specialty, or location..."
              className="w-full rounded-md border border-slate-300 py-2 pl-10 pr-3 text-sm placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-500 disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="h-4 w-4" />
                Search
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onToggle}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-4 w-4" />
                Less Filters
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" />
                More Filters
              </>
            )}
          </button>
        </form>
      </div>

      {/* Advanced Filters Panel */}
      {isExpanded && (
        <div className="border-t border-slate-200 bg-slate-50 p-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* State */}
            <div>
              <label className="block text-xs font-medium text-slate-700">
                State
              </label>
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value.toUpperCase())}
                maxLength={2}
                placeholder="e.g., CA, TX, NY"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm uppercase placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              />
            </div>

            {/* Specialty */}
            <div>
              <label className="block text-xs font-medium text-slate-700">
                Specialty
              </label>
              <input
                type="text"
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
                placeholder="ICU, ER, Med-Surg..."
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              />
            </div>

            {/* Shift Type */}
            <div>
              <label className="block text-xs font-medium text-slate-700">
                Shift Preference
              </label>
              <select
                value={shiftType}
                onChange={(e) => setShiftType(e.target.value as ShiftFilter)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              >
                <option value="ANY">Any Shift</option>
                <option value="DAYS">Days</option>
                <option value="NIGHTS">Nights</option>
                <option value="ROTATING">Rotating</option>
              </select>
            </div>

            {/* Min Rate */}
            <div>
              <label className="block text-xs font-medium text-slate-700">
                Min Hourly Rate
              </label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">
                  $
                </span>
                <input
                  type="number"
                  min={0}
                  step="5"
                  value={minRate ?? ""}
                  onChange={(e) =>
                    setMinRate(
                      e.target.value === "" ? undefined : Number(e.target.value)
                    )
                  }
                  placeholder="45"
                  className="w-full rounded-md border border-slate-300 py-2 pl-7 pr-3 text-sm placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                />
              </div>
            </div>
          </div>

          {/* Additional Options */}
          <div className="mt-4 flex items-center justify-between">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isUrgent}
                onChange={(e) => setIsUrgent(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-slate-700">Show urgent positions only</span>
            </label>
          </div>
        </div>
      )}
    </section>
  );
}

// =============================================================================
// Sort Dropdown Component
// =============================================================================

interface SortDropdownProps {
  value: SortOption;
  onChange: (option: SortOption) => void;
}

function SortDropdown({ value, onChange }: SortDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  const options: { value: SortOption; label: string }[] = [
    { value: "relevance", label: "Most Relevant" },
    { value: "pay_high", label: "Highest Pay" },
    { value: "pay_low", label: "Lowest Pay" },
    { value: "date_new", label: "Newest First" },
    { value: "date_old", label: "Oldest First" },
  ];

  const currentLabel = options.find((o) => o.value === value)?.label || "Sort By";

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
      >
        <ArrowUpDown className="h-4 w-4" />
        {currentLabel}
        <ChevronDown className="h-3.5 w-3.5" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 z-20 mt-1 min-w-[180px] rounded-md border border-slate-200 bg-white py-1 shadow-lg">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={cn(
                  "block w-full px-3 py-2 text-left text-sm hover:bg-slate-50",
                  value === option.value && "bg-emerald-50 text-emerald-700 font-medium"
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// =============================================================================
// Pagination Component
// =============================================================================

interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function Pagination({ page, totalPages, onPageChange }: PaginationProps) {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between text-sm text-slate-600">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        className="rounded-md border border-slate-200 bg-white px-3 py-1.5 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Previous
      </button>
      <span>
        Page {page} of {totalPages}
      </span>
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
        className="rounded-md border border-slate-200 bg-white px-3 py-1.5 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Next
      </button>
    </div>
  );
}

// =============================================================================
// Main Page Component
// =============================================================================

export default function NurseJobsPage() {
  // Filter state
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [shiftType, setShiftType] = useState<ShiftFilter>("ANY");
  const [minRate, setMinRate] = useState<number | undefined>();
  const [isUrgent, setIsUrgent] = useState(false);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortOption>("relevance");
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Data hooks
  const appliedJobIds = useAppliedJobIds();
  const { data, isLoading, isFetching } = useJobs({
    q: q || undefined,
    state: state || undefined,
    specialty: specialty || undefined,
    shift_type: shiftType,
    min_rate: minRate,
    is_urgent: isUrgent || undefined,
    page,
    page_size: 10,
  });

  const applyMutation = useApplyToJob();

  // Computed
  const total = data?.total ?? 0;
  const pageSize = data?.page_size ?? 10;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  let jobs = data?.results ?? [];

  // Client-side sorting (since backend may not support all sort options)
  jobs = [...jobs].sort((a, b) => {
    switch (sortBy) {
      case "pay_high":
        return (b.nurse_base_rate_hour ?? 0) - (a.nurse_base_rate_hour ?? 0);
      case "pay_low":
        return (a.nurse_base_rate_hour ?? 0) - (b.nurse_base_rate_hour ?? 0);
      case "date_new":
        return (b.start_date ?? "").localeCompare(a.start_date ?? "");
      case "date_old":
        return (a.start_date ?? "").localeCompare(b.start_date ?? "");
      default:
        return 0;
    }
  });

  // Handlers
  const handleSearch = () => {
    setPage(1);
  };

  const handleApply = async (jobId: string) => {
    if (appliedJobIds.has(jobId)) return;
    try {
      await applyMutation.mutateAsync({ jobId });
    } catch (err) {
      console.error("Failed to apply:", err);
    }
  };

  const handleSaveSearch = () => {
    // TODO: Implement save search functionality when backend supports it
    alert("Save search feature coming soon!");
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            Find Your Next Assignment
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Browse travel nursing opportunities across the country. Your compliance
            is automatically verified when facilities review your application.
          </p>
        </div>
        <NotificationsBell />
      </header>

      {/* Filters */}
      <div className="mt-6">
        <AdvancedFilters
          q={q}
          setQ={setQ}
          state={state}
          setState={setState}
          specialty={specialty}
          setSpecialty={setSpecialty}
          shiftType={shiftType}
          setShiftType={setShiftType}
          minRate={minRate}
          setMinRate={setMinRate}
          isUrgent={isUrgent}
          setIsUrgent={setIsUrgent}
          onSearch={handleSearch}
          isLoading={isFetching}
          isExpanded={filtersExpanded}
          onToggle={() => setFiltersExpanded(!filtersExpanded)}
        />
      </div>

      {/* Results */}
      <section className="mt-6 space-y-4">
        {/* Results Header */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-600">
            {isLoading
              ? "Loading..."
              : `${total} job${total !== 1 ? "s" : ""} found`}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleSaveSearch}
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              <Bookmark className="h-4 w-4" />
              Save Search
            </button>
            <SortDropdown value={sortBy} onChange={setSortBy} />
          </div>
        </div>

        {/* Job Cards */}
        {isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
            <RefreshCw className="mx-auto h-8 w-8 animate-spin text-slate-400" />
            <p className="mt-2 text-sm text-slate-500">Loading jobs...</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="rounded-lg border border-slate-200 bg-white p-12 text-center">
            <Briefcase className="mx-auto h-12 w-12 text-slate-300" />
            <h3 className="mt-4 text-base font-medium text-slate-900">
              No jobs found
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              Try adjusting your filters or search for different keywords.
            </p>
            <button
              type="button"
              onClick={() => {
                setQ("");
                setState("");
                setSpecialty("");
                setShiftType("ANY");
                setMinRate(undefined);
                setIsUrgent(false);
                setPage(1);
              }}
              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                applied={appliedJobIds.has(job.id)}
                onApply={handleApply}
                isApplying={applyMutation.isPending}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && jobs.length > 0 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        )}
      </section>
    </div>
  );
}
